import React, { useState, useEffect } from 'react';
import PhoneScreenshot from '../../../components/common/PhoneScreenshot';

const Screenshots = ({ screenshotsData, theme }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [phoneHeight, setPhoneHeight] = useState('600px');

  // Update phone height based on screen width
  useEffect(() => {
    const updatePhoneHeight = () => {
      const width = window.innerWidth;
      if (width < 375) {
        setPhoneHeight('280px');
      } else if (width < 480) {
        setPhoneHeight('320px');
      } else if (width < 768) {
        setPhoneHeight('380px');
      } else if (width < 1024) {
        setPhoneHeight('440px');
      } else if (width < 1280) {
        setPhoneHeight('520px');
      } else {
        setPhoneHeight('600px');
      }
    };

    updatePhoneHeight();
    window.addEventListener('resize', updatePhoneHeight);
    return () => window.removeEventListener('resize', updatePhoneHeight);
  }, []);
  
  const styles = {
    container: {
      paddingTop: '15px',
      paddingBottom: '15px',
      scrollMarginTop: '80px',
      textAlign: 'center',
      position: 'relative'
    },
    content: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 1rem'
    },
    header: {
      textAlign: 'center',
      marginBottom: '2rem'
    },
    title: {
      fontSize: 'clamp(1.75rem, 5vw, 2.5rem)',
      fontWeight: '800',
      color: theme?.text?.primary,
      marginBottom: '0.75rem'
    },
    subtitle: {
      fontSize: 'clamp(0.95rem, 2.5vw, 1.125rem)',
      color: theme?.text?.secondary,
      maxWidth: '800px',
      margin: '0 auto',
      marginBottom: '1.5rem',
      lineHeight: '1.6',
      padding: '0 0.5rem'
    },
    sliderWrapper: {
      background: theme?.background?.card,
      borderRadius: '0.75rem',
      padding: '0.5rem',
      overflow: 'hidden' // Prevent content from overflowing
    },
    featureText: {
      color: theme?.text?.secondary
    },
    featureIcon: {
      color: theme?.ui?.warning
    },
    navButton: {
      background: theme?.background?.card,
      borderColor: theme?.ui?.border,
      color: theme?.text?.primary
    },
    indicator: (isActive) => ({
      borderColor: 'transparent',
      background: isActive ? theme?.text?.primary : theme?.text?.tertiary,
      boxShadow: isActive ? `0 0 10px ${theme?.text?.primary}50` : 'none'
    })
  };

  const totalSlides = screenshotsData.slides.length;

  const goToSlide = (slideIndex) => {
    setCurrentSlide(Math.max(0, Math.min(slideIndex, totalSlides - 1)));
  };

  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + totalSlides) % totalSlides);
  };

  // Touch/swipe handling
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
    
    // Reset touch values
    setTouchStart(0);
    setTouchEnd(0);
  };

  return (
    <section id="screenshots" style={styles.container}>
      <div style={styles.content}>
        <div style={styles.header}>
          <h2 style={styles.title}>{screenshotsData.title}</h2>
          <p style={styles.subtitle}>{screenshotsData.subtitle}</p>
        </div>

        {/* Slider Container */}
        <div className="screenshots-slider-container">
          {/* Slider wrapper */}
          <div 
            className="screenshots-slider-wrapper"
            style={styles.sliderWrapper}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div 
              className="screenshots-slides-track"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {screenshotsData.slides.map((slide, index) => (
                <div key={slide.id} className="screenshots-slide">
                  {/* Phone Mockup */}
                  <div className="screenshots-phone-container">
                    <PhoneScreenshot
                      imageSrc={slide.image}
                      alt={slide.alt}
                      theme={theme}
                      showPerspective={false}
                      frameBackground={theme?.primary?.gray?.[500]}
                      height={phoneHeight}
                    />
                  </div>

                  {/* Content */}
                  <div className="screenshots-content">
                    <h3 className="screenshots-slide-title" style={{ color: theme?.text?.primary }}>
                      {slide.title}
                    </h3>
                    <p className="screenshots-slide-description" style={{ color: theme?.text?.secondary }}>
                      {slide.description}
                    </p>
                    <ul className="screenshots-features-list">
                      {slide.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="screenshots-feature-item" style={styles.featureText}>
                          <span className="screenshots-feature-icon" style={styles.featureIcon}>✨</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Controls */}
          <div className="screenshots-controls">
            <button
              onClick={prevSlide}
              disabled={currentSlide === 0}
              className="screenshots-nav-btn"
              style={styles.navButton}
            >
              ‹
            </button>

            {/* Indicators */}
            <div className="screenshots-indicators">
              {screenshotsData.slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`screenshots-indicator ${index === currentSlide ? 'active' : ''}`}
                  style={styles.indicator(index === currentSlide)}
                />
              ))}
            </div>

            <button
              onClick={nextSlide}
              className="screenshots-nav-btn"
              style={styles.navButton}
            >
              ›
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .screenshots-slider-container {
          max-width: 72rem;
          margin: 0 auto;
          opacity: 0;
          animation: screenshotsFadeInUp 1s ease-out 0.6s forwards;
        }
        
        @keyframes screenshotsFadeInUp {
          from {
            opacity: 0;
            transform: translateY(50px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .screenshots-slider-wrapper {
          position: relative;
          backdrop-filter: blur(12px);
          overflow: hidden;
        }
        
        .screenshots-slides-track {
          display: flex;
          transition: transform 500ms ease-out;
        }
        
        .screenshots-slide {
          min-width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 1.5rem 1rem;
          gap: 1.5rem;
          box-sizing: border-box;
        }
        
        /* Phone container - mobile first */
        .screenshots-phone-container {
          flex-shrink: 0;
          display: flex;
          justify-content: center;
          align-items: center;
        }
        
        /* Content area - mobile first */
        .screenshots-content {
          flex: 1;
          max-width: 100%;
          text-align: center;
        }
        
        .screenshots-slide-title {
          font-size: 1.25rem;
          font-weight: 700;
          margin-bottom: 0.75rem;
          line-height: 1.3;
        }
        
        .screenshots-slide-description {
          font-size: 0.875rem;
          line-height: 1.6;
          margin-bottom: 1rem;
        }
        
        .screenshots-features-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        
        .screenshots-feature-item {
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.8rem;
        }
        
        .screenshots-feature-icon {
          margin-right: 0.5rem;
          font-size: 0.875rem;
        }
        
        /* Controls */
        .screenshots-controls {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          margin-top: 1.5rem;
        }
        
        .screenshots-nav-btn {
          width: 2.5rem;
          height: 2.5rem;
          border-radius: 50%;
          backdrop-filter: blur(8px);
          border: 1px solid;
          font-size: 1.25rem;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: transform 200ms, opacity 200ms;
        }
        
        .screenshots-nav-btn:hover:not(:disabled) {
          transform: scale(1.1);
        }
        
        .screenshots-nav-btn:active:not(:disabled) {
          transform: scale(0.95);
        }
        
        .screenshots-nav-btn:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }
        
        .screenshots-indicators {
          display: flex;
          gap: 0.5rem;
        }
        
        .screenshots-indicator {
          width: 0.625rem;
          height: 0.625rem;
          border-radius: 50%;
          border: 2px solid transparent;
          cursor: pointer;
          transition: transform 300ms, box-shadow 300ms;
        }
        
        .screenshots-indicator.active {
          transform: scale(1.25);
        }
        
        .screenshots-indicator:hover:not(.active) {
          transform: scale(1.1);
        }
        
        /* Small mobile (320px+) - no special phone rules needed */
        
        /* Medium mobile (375px+) */
        @media (min-width: 375px) {
          .screenshots-slide {
            padding: 1.5rem;
          }
          
          .screenshots-slide-title {
            font-size: 1.375rem;
          }
          
          .screenshots-slide-description {
            font-size: 0.9375rem;
          }
          
          .screenshots-feature-item {
            font-size: 0.875rem;
          }
        }
        
        /* Large mobile (480px+) */
        @media (min-width: 480px) {
          .screenshots-slide {
            padding: 2rem;
            gap: 2rem;
          }
          
          .screenshots-slide-title {
            font-size: 1.5rem;
          }
          
          .screenshots-slide-description {
            font-size: 1rem;
          }
        }
        
        /* Tablet (768px+) - switch to horizontal layout */
        @media (min-width: 768px) {
          .screenshots-slide {
            flex-direction: row;
            padding: 2.5rem;
            gap: 3rem;
          }
          
          .screenshots-phone-container {
            order: 2;
          }
          
          .screenshots-content {
            order: 1;
            text-align: left;
            max-width: 28rem;
          }
          
          .screenshots-slide-title {
            font-size: 1.75rem;
            margin-bottom: 1rem;
          }
          
          .screenshots-slide-description {
            font-size: 1.0625rem;
            margin-bottom: 1.5rem;
          }
          
          .screenshots-features-list {
            gap: 0.75rem;
          }
          
          .screenshots-feature-item {
            justify-content: flex-start;
            font-size: 1rem;
          }
          
          .screenshots-feature-icon {
            margin-right: 0.75rem;
          }
          
          .screenshots-controls {
            gap: 1.25rem;
            margin-top: 2rem;
          }
          
          .screenshots-nav-btn {
            width: 3rem;
            height: 3rem;
            font-size: 1.5rem;
          }
          
          .screenshots-indicator {
            width: 0.75rem;
            height: 0.75rem;
          }
        }
        
        /* Desktop (1024px+) */
        @media (min-width: 1024px) {
          .screenshots-slide {
            padding: 3rem;
            gap: 4rem;
          }
          
          .screenshots-slide-title {
            font-size: 2rem;
          }
          
          .screenshots-slide-description {
            font-size: 1.125rem;
          }
          
          .screenshots-controls {
            margin-top: 2.5rem;
          }
        }
        
        /* Large desktop (1280px+) */
        @media (min-width: 1280px) {
          .screenshots-slide {
            padding: 3.5rem;
            gap: 5rem;
          }
          
          .screenshots-slide-title {
            font-size: 2.25rem;
          }
        }
      `}</style>
    </section>
  );
};

export default Screenshots;